import {
  clearMarkFields,
  elementMediaId,
  setPlainText,
  snapToFrame,
  videoDuration,
} from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { orpc } from "../lib/api";
import { errorMessage } from "../lib/utils";
import { fitView, zoomAt } from "./Canvas";
import {
  addShape,
  addText,
  copySelection,
  deleteSelection,
  duplicateSelection,
  groupSelection,
  nudgeSelection,
  parseClipboard,
  pasteData,
  reorderSelection,
  toggleHidden,
  toggleLock,
  ungroupSelection,
} from "./commands";
import { toggleDrawMode, useDrawTool } from "./DrawTool";
import type { EditorEnv } from "./env";
import { sampleSelectionColor } from "./eyedropper";
import { useLeftNav } from "./left-nav";
import type { EditorStore } from "./store";
import { copyStyle, pasteStyle } from "./style-clipboard";
import { deleteSelectedClip, duplicateSelectedClip, splitAtPlayhead } from "./video/commands";
import type { VideoStore } from "./video/store";
import { useViewPrefs } from "./view";

// ---------------------------------------------------------------------------
// Keyboard shortcuts and clipboard
// ---------------------------------------------------------------------------

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/** Video editor keys (play, split, frame steps); true when the key was handled. */
function videoKey(event: KeyboardEvent, store: EditorStore, video: VideoStore): boolean {
  const state = store.getState();
  const player = video.getState();
  const key = event.key.toLowerCase();
  const mod = event.metaKey || event.ctrlKey;
  const handle = (fn: () => void) => {
    event.preventDefault();
    fn();
    return true;
  };
  if (event.code === "Space" && !mod) return handle(() => player.toggle());
  const noElements = state.selection.length === 0;
  if ((key === "arrowleft" || key === "arrowright") && noElements && !mod) {
    const fps = state.doc.timeline?.fps ?? 30;
    const step = event.shiftKey ? 1000 : 1000 / fps;
    const direction = key === "arrowleft" ? -1 : 1;
    const duration = videoDuration(state.doc);
    return handle(() =>
      player.seek(
        Math.max(0, Math.min(duration, snapToFrame(player.time + direction * step, fps))),
      ),
    );
  }
  if (key === "home") return handle(() => player.seek(0));
  if (key === "end") return handle(() => player.seek(videoDuration(state.doc)));
  if (key === "escape" && player.selectedClip && noElements) {
    return handle(() => player.selectClip(null));
  }
  if (state.readOnly) return false;
  if (key === "s" && !mod && !event.altKey) return handle(() => splitAtPlayhead(store, video));
  if ((key === "delete" || key === "backspace") && noElements && player.selectedClip) {
    return handle(() => deleteSelectedClip(store, video));
  }
  if (mod && key === "d" && noElements && player.selectedClip) {
    return handle(() => duplicateSelectedClip(store, video));
  }
  return false;
}

export function useShortcuts(
  store: EditorStore,
  env: EditorEnv,
  viewport: React.RefObject<HTMLDivElement | null>,
  actions: { openExport: () => void; save: () => void },
  video: VideoStore | null = null,
) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTyping(event.target)) return;
      if (document.querySelector("[role=dialog]")) return;
      if (video && videoKey(event, store, video)) return;
      const state = store.getState();
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      const run = (fn: () => void) => {
        event.preventDefault();
        fn();
      };
      if (mod && key === "z") return run(() => (event.shiftKey ? state.redo() : state.undo()));
      if (mod && key === "y") return run(() => state.redo());
      if (mod && key === "s") return run(actions.save);
      if (mod && key === "e") return run(actions.openExport);
      // Format übertragen (⌥⌘C / ⌥⌘V; the Option key changes `key`, so match the code).
      if (mod && event.altKey && event.code === "KeyC") return run(() => copyStyle(store));
      if (mod && event.altKey && event.code === "KeyV" && !state.readOnly) {
        return run(() => pasteStyle(store));
      }
      if (mod && (key === "=" || key === "+"))
        return run(() => zoomAt(store, 1.25, center(viewport)));
      if (mod && key === "-") return run(() => zoomAt(store, 0.8, center(viewport)));
      if (mod && key === "0")
        return run(
          () => viewport.current && fitView(store, viewport.current.getBoundingClientRect()),
        );
      if (mod && key === "1") {
        return run(() => {
          const c = center(viewport);
          zoomAt(store, 1 / state.zoom, c);
        });
      }
      if (key === "pagedown") return run(() => state.goToPage(state.pageIndex + 1));
      if (key === "pageup") return run(() => state.goToPage(state.pageIndex - 1));
      if (key === "escape") {
        return run(() => {
          if (useDrawTool.getState().active) useDrawTool.getState().setActive(false);
          else if (state.croppingId) state.setCropping(null);
          else state.select([]);
        });
      }
      // View of the design editor: rulers, guides, layout grid (also when only viewing).
      if (event.shiftKey && !mod && !event.altKey && state.doc.kind === "design") {
        const view = useViewPrefs.getState();
        if (key === "r") return run(() => view.toggle("rulers"));
        if (key === "h") return run(() => view.toggle("guides"));
        if (key === "g") return run(() => view.toggle("grid"));
      }
      if (!mod && !event.altKey && event.shiftKey && key === "l") {
        return run(() => useLeftNav.getState().open("layers"));
      }
      if (state.readOnly) return;
      if (!mod && !event.altKey && !event.shiftKey && key === "i") {
        return run(() => {
          if (!event.repeat) void sampleSelectionColor(store);
        });
      }
      if (mod && key === "a") {
        return run(() => state.select(state.doc.pages[state.pageIndex]?.children ?? []));
      }
      if (mod && key === "d") return run(() => duplicateSelection(store));
      if (mod && key === "g")
        return run(() => (event.shiftKey ? ungroupSelection(store) : groupSelection(store)));
      if (mod && key === "l") return run(() => toggleLock(store));
      if (mod && event.shiftKey && key === "h") return run(() => toggleHidden(store));
      if (mod && (event.key === "]" || event.code === "BracketRight")) {
        return run(() => reorderSelection(store, event.shiftKey ? "front" : "forward"));
      }
      if (mod && (event.key === "[" || event.code === "BracketLeft")) {
        return run(() => reorderSelection(store, event.shiftKey ? "back" : "backward"));
      }
      if (key === "delete" || key === "backspace") return run(() => deleteSelection(store));
      if (key.startsWith("arrow") && state.selection.length > 0) {
        const step = event.shiftKey ? 10 : 1;
        const dx = key === "arrowleft" ? -step : key === "arrowright" ? step : 0;
        const dy = key === "arrowup" ? -step : key === "arrowdown" ? step : 0;
        return run(() => nudgeSelection(store, dx, dy));
      }
      if (key === "enter" && state.selection.length === 1) {
        const element = state.doc.elements[state.selection[0]!];
        if (element?.type === "text") return run(() => state.setEditingText(element.id));
        if (element?.type === "image") return run(() => state.setCropping(element.id));
      }
      if (!mod && !event.altKey) {
        if (key === "t") return run(() => addText(store, "body"));
        if (key === "r") return run(() => addShape(store, "rect"));
        if (key === "o") return run(() => addShape(store, "ellipse"));
        if (key === "l") return run(() => addShape(store, "line"));
        if (key === "p") return run(() => toggleDrawMode(store));
      }
      // Text formatting shortcuts on a selected text element (the whole text)
      const strike = mod && event.shiftKey && key === "x";
      if ((mod && !event.shiftKey && (key === "b" || key === "i" || key === "u")) || strike) {
        const texts = state.selection.filter((id) => state.doc.elements[id]?.type === "text");
        if (texts.length === 0) return;
        return run(() =>
          state.update((draft) => {
            for (const id of texts) {
              const element = draft.elements[id];
              if (element?.type !== "text") continue;
              if (key === "b") {
                element.style.fontWeight = element.style.fontWeight >= 700 ? 400 : 700;
                clearMarkFields(element, ["fontWeight"]);
              }
              if (key === "i") {
                element.style.italic = !element.style.italic;
                clearMarkFields(element, ["italic"]);
              }
              if (key === "u") {
                element.style.underline = !element.style.underline;
                clearMarkFields(element, ["underline"]);
              }
              if (strike) {
                element.style.strike = !element.style.strike;
                clearMarkFields(element, ["strike"]);
              }
            }
          }),
        );
      }
    };

    const onCopy = (event: ClipboardEvent) => {
      if (isTyping(event.target)) return;
      const text = copySelection(store);
      if (!text) return;
      event.preventDefault();
      event.clipboardData?.setData("text/plain", text);
    };
    const onCut = (event: ClipboardEvent) => {
      if (isTyping(event.target) || store.getState().readOnly) return;
      onCopy(event);
      deleteSelection(store);
    };
    const onPaste = (event: ClipboardEvent) => {
      if (isTyping(event.target) || store.getState().readOnly) return;
      const data = event.clipboardData;
      if (!data) return;
      const files = [...data.files].filter((file) => file.type.startsWith("image/"));
      if (files.length > 0) {
        event.preventDefault();
        env.uploadAndInsert(files);
        return;
      }
      const text = data.getData("text/plain");
      const parsed = parseClipboard(text);
      event.preventDefault();
      if (parsed) {
        // The editor only knows the newest media of the library (and what the document
        // uses), so ask the server for the rest: it only answers for this organization.
        const unknown = new Set<string>();
        for (const element of Object.values(parsed.elements)) {
          const assetId = elementMediaId(element);
          if (assetId !== null && !env.assets.has(assetId)) unknown.add(assetId);
        }
        if (unknown.size === 0) {
          pasteData(store, parsed);
          return;
        }
        void Promise.all(
          [...unknown].map((assetId) =>
            queryClient
              .fetchQuery(orpc.media.get.queryOptions({ input: { orgId: env.orgId, assetId } }))
              .then(() => true)
              .catch(() => false),
          ),
        ).then((found) => {
          if (found.every(Boolean)) pasteData(store, parsed);
          else {
            toast.error(
              "Die kopierten Bilder gehören zu einer anderen Organisation oder wurden gelöscht und können hier nicht eingefügt werden.",
            );
          }
        });
      } else if (text.trim()) {
        const id = addText(store, "body");
        store.getState().setEditingText(null);
        store.getState().update((draft) => {
          const element = draft.elements[id];
          if (element?.type === "text") setPlainText(element, text.trim().slice(0, 5000));
        });
      } else {
        pasteData(store);
      }
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("copy", onCopy);
    window.addEventListener("cut", onCut);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("cut", onCut);
      window.removeEventListener("paste", onPaste);
    };
  }, [store, env, viewport, actions, video, queryClient]);
}

function center(viewport: React.RefObject<HTMLDivElement | null>) {
  const rect = viewport.current?.getBoundingClientRect();
  return rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 0, y: 0 };
}

export function useMediaQueryInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: orpc.media.key() });
}

export { errorMessage };
